// The shape written to mirror_reminders.message, and how to read it back.
//
// The column used to hold pre-translated text ({pt, en, nl}), which froze the
// translation at creation time and left the dispatcher with no way to know the
// user's language — so OneSignal picked by DEVICE locale. It now carries the
// template KEY (or the user's raw text), and the dispatcher renders it fresh in
// the user's current language on every send.
//
// Both layers must agree on this shape:
//   writer/reader here          — src/lib/reminderMessage.ts
//   dispatcher                  — supabase/functions/_shared/reminderTemplates.ts
// The two template tables are pinned to each other by
// src/test/reminderTemplates.test.ts.

import type { Lang } from "@/i18n/locale";

/** Categories whose copy is a canned, translatable string. */
export const REMINDER_TEMPLATE_KEYS = ["hydration", "movement", "motivation", "sleep"] as const;

export type ReminderTemplateKey = (typeof REMINDER_TEMPLATE_KEYS)[number];

/** i18n key for a template category. Single source for both layers. */
export function reminderTemplateI18nKey(key: ReminderTemplateKey): string {
  return `settings.reminders.template.${key}`;
}

export function isReminderTemplateKey(value: unknown): value is ReminderTemplateKey {
  return typeof value === "string" &&
    (REMINDER_TEMPLATE_KEYS as readonly string[]).indexOf(value) !== -1;
}

/**
 * What lands in the column.
 *
 * `key` / `text` are what the dispatcher reads. `pt`/`en`/`nl` are written
 * ALONGSIDE them on purpose — see buildReminderMessage.
 */
export interface ReminderMessage {
  key?: ReminderTemplateKey;
  text?: string;
  pt?: string;
  en?: string;
  nl?: string;
}

/**
 * Build the value to store.
 *
 * DUAL-WRITE, deliberately: the frontend auto-deploys from main while edge
 * functions are deployed by hand, so for some window after this merges the OLD
 * dispatcher — which only understands {pt,en,nl} — is still the one running. A
 * key-only row would hit its "malformed message" branch, which defers the
 * reminder +24h and repeats forever, silently. Writing both shapes makes the
 * row correct under either dispatcher; the redundant text can be dropped once
 * the new one is deployed everywhere.
 *
 * @param templateKey null for custom/user-edited text
 * @param text        the raw text (custom) or the resolved template preview
 * @param resolve     reads an i18n key in a specific language
 */
/**
 * Os idiomas que o dual-write precisa resolver.
 *
 * São exatamente os três slots legados de `ReminderMessage` ({pt,en,nl}) — e a
 * lista existe como DADO porque, com os dicionários em code-splitting, quem
 * chama `buildReminderMessage` tem de garantir que esses três estejam em
 * memória antes. Duas listas escritas à mão nos dois call sites divergiriam no
 * dia em que um slot mudar.
 *
 * Três idiomas e não nove de propósito: o dual-write não é i18n do produto, é
 * compatibilidade com o dispatcher antigo, que só entende estes campos.
 */
export const DUAL_WRITE_LANGS: readonly Lang[] = ["PT-BR", "EN", "NL"];

export function buildReminderMessage(
  templateKey: ReminderTemplateKey | null,
  text: string,
  resolve: (lang: Lang, key: string) => string | undefined,
): ReminderMessage | null {
  if (templateKey) {
    const i18nKey = reminderTemplateI18nKey(templateKey);
    const pt = resolve("PT-BR", i18nKey) || text.trim();
    const en = resolve("EN", i18nKey) || text.trim();
    const nl = resolve("NL", i18nKey) || text.trim();
    if (!pt && !en && !nl) return null;
    return { key: templateKey, pt: pt || en || nl, en: en || pt || nl, nl: nl || en || pt };
  }
  const trimmed = text.trim();
  if (!trimmed) return null;
  // Free text is never translatable — the same string in every slot.
  return { text: trimmed, pt: trimmed, en: trimmed, nl: trimmed };
}

/**
 * Render a stored message for display in the UI.
 *
 * Mirrors the dispatcher's precedence (key → text → legacy slots) so the list
 * shows exactly what the push will say. The old reader only ever consulted
 * `pt` and `en`, so a Dutch user read the English text even on rows that had a
 * Dutch translation.
 */
export function readReminderMessage(
  message: ReminderMessage | null | undefined,
  lang: Lang,
  t: (key: string) => string,
): string {
  if (!message) return "";
  if (isReminderTemplateKey(message.key)) return t(reminderTemplateI18nKey(message.key));
  if (typeof message.text === "string" && message.text.trim()) return message.text.trim();
  const pt = message.pt?.trim() || "";
  const en = message.en?.trim() || "";
  const nl = message.nl?.trim() || "";
  if (lang === "EN") return en || pt || nl;
  if (lang === "NL") return nl || en || pt;
  return pt || en || nl;
}
